import { AccessibleForm, Alert, Button, Heading, TextInput } from '@digitools/honest-react';
import { Col, Container, Row } from '@digitools/honest-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ExperienceContext } from 'src/providers/ExperienceProvider';
import Captcha from '../../../../../components/captcha/Captcha';
import { Link, useSearchParams } from 'react-router-dom';
import { getEnvVariable, windowAssign } from 'src/utils/envUtils';
import useTealium from 'src/hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import axios from "axios";
import useMediaQuery from "src/hooks/useMediaQuery";
import { GrayItalicP } from 'src/components/Formatting/StyledComponents';
import useValidCaptcha from 'src/hooks/fieldValidation/useValidCaptcha';
const CompanyCode = (props) => {
    const { trackEvent } = useTealium();
    const { validEmployerRegCode, setValidEmployerRegCode, employeeRegistrationAllowed, validateCompanyCode, getLocalDisplayText, companyCode, companyName, resetCaptcha, setResetCaptcha, captchaInitialValue } = props;
    const validateCaptchaRequired = useValidCaptcha();
    const formApi = useRef(null);
    const { language } = useContext(ExperienceContext);
    const [resetCaptchaState, setCaptchaState] = useState(false);
    const [queryParams] = useSearchParams();
    const token = queryParams.get('token') || undefined;
    const state = queryParams.get('state') || undefined;
    const [unexpectedAuthError, setUnexpectedAuthError] = useState(false);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isSSOERRegEnabled = getEnvVariable('LfgSsoErRegistrationEnabled');
    const showCaptcha = !(isSSOERRegEnabled && token !== undefined);
    const validateAuthToken = async (token, state, regCode) => {
        await axios.post(`/auth-service/services/v1/auth0/app-metadata`, {
            token: token,
            state: state,
            appMetadata: {
                registrationCode: regCode
            },
        }).then(response => {
            if (response.headers['location']) {
                trackEvent({
                    event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
                    event_name: 'yes confirm',
                    event_type: 'register',
                    event_version: TEALIUM_EVENT.EVENT_VERSION.VERIFY_COMPANY_CODE,
                });
                windowAssign(response.headers['location']);
            }
            else {
                setUnexpectedAuthError(true);
            }
        }).catch(() => {
            setUnexpectedAuthError(true);
        });
    };
    useEffect(() => {
        if (resetCaptcha && formApi.current) {
            setCaptchaState(true);
            setResetCaptcha?.(false);
        }
    }, [resetCaptcha]);
    useEffect(() => {
        //if valid company code and user exists show "access benefits for:" message with yes/cancel buttons
        if (validEmployerRegCode === false) {
            formApi.current?.setError('companyCode', getLocalDisplayText('companyCodeError'));
        }
        if (!employeeRegistrationAllowed) {
            formApi.current?.setError('companyCode', getLocalDisplayText('employeeCompanyCodeError'));
            trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_name: getLocalDisplayText('employeeCompanyCodeError'),
                event_type: TEALIUM_EVENT.EVENT_TYPE.MIGRATION,
                event_version: TEALIUM_EVENT.EVENT_VERSION.BLOCK_REGISTRATION,
            });
        }
    }, [validEmployerRegCode, employeeRegistrationAllowed, language]);
    const getFormApi = (api) => {
        formApi.current = api;
    };
    return (<>
        <Container>
          <Row>
            <Col>
              <Heading elemType={isSSOERRegEnabled && token ? 'h2' : 'h1'} elemStyle={'h2'} data-testid={'pageHeading'} id='pageHeading' color='secondaryDarker'>
                {isSSOERRegEnabled && token ? getLocalDisplayText('accountAccess') : getLocalDisplayText('employerBrokerPageHeading')}
              </Heading>
            </Col>
          </Row>
          <Row>
            <Col>
              {(!isSSOERRegEnabled || validEmployerRegCode !== true) && (<p id='registrationInfoText' data-testid={'registrationInfoText'}>
                  {isSSOERRegEnabled && token
                ? getLocalDisplayText('registrationInfoTextCompanyCodeCcm')
                : getLocalDisplayText('registrationInfoTextCompanyCode')}
                </p>)}
              {(!isSSOERRegEnabled || !token) && <p>{getLocalDisplayText('companyCodeToolTip')}</p>}
            </Col>
          </Row>
          {isSSOERRegEnabled && validEmployerRegCode === true && token !== undefined && state !== undefined ? (<>
              <p className={'mt-0'} data-testid={'accessClaimsPortalText'}>
                {getLocalDisplayText('accessClaimsPortalText')}
                <b>
                  <i>{companyName}</i>
                </b>
                ?{' '}
              </p>
              <Row>
                <Button onClick={() => validateAuthToken(token, state, companyCode)} className={isMobile ? 'ml-2 mb-2 w-100' : 'ml-2 mb-2'} buttonType={'primary'}>
                  {getLocalDisplayText('confirmButton')}
                </Button>
                <Button onClick={() => setValidEmployerRegCode(null)} className={isMobile ? 'ml-2 mb-2 w-100' : 'ml-2 mb-2'} buttonType={'secondary'}>
                  {getLocalDisplayText('cancelButtonLabel')}
                </Button>
              </Row>
              <GrayItalicP>{getLocalDisplayText('suggestLogOffMessage')}</GrayItalicP>
              {unexpectedAuthError && <Alert type={'error'}>{getLocalDisplayText('unexpectedAuthError')}</Alert>}
            </>) : (<AccessibleForm onSubmit={validateCompanyCode} getApi={getFormApi} className={'mt-4'}>
              <Row>
                <Col md={'6'}>
                  <TextInput required={true} field='companyCode' label={getLocalDisplayText('companyCodeLabel')} validateOnBlur={true} data-testid={'companyCode'}/>
                </Col>
              </Row>
              {showCaptcha && (<Row data-testid={'captchaRow'} className={'d-flex justify-content-start mt-3'}>
                  <Col>
                    <Captcha aria-label={'solve captcha'} field={'captcha'} resetCaptcha={resetCaptchaState} setCaptchaState={setCaptchaState} required={true} validate={validateCaptchaRequired} validateOnBlur={true} validateOnChange={true} data-testid={'captcha'} initialValue={captchaInitialValue}/>
                  </Col>
                </Row>)}
              <Button className={token && isMobile ? 'mt-3 mb-2 w-100' : 'mt-3 mb-2 '} data-testid={'validateCodeButton'} buttonType={'primary'} id={'validateButton'} type={'submit'}>
                {getLocalDisplayText('validateButton')}
              </Button>
              {(!isSSOERRegEnabled || !token) && (<Button className={'mt-3 mb-2 d-inline-flex align-items-center justify-content-center'} buttonType={'secondary'} data-testid={'returnToLoginButton'} {...{ as: Link, to: '/public/login' }}>
                  {getLocalDisplayText('cancelButtonLabel')}
                </Button>)}
              {isSSOERRegEnabled && token && (<Row className={'mt-2'}>
                  <Col>
                    <GrayItalicP>{getLocalDisplayText('companyCodeCcmToolTip')}</GrayItalicP>
                  </Col>
                </Row>)}
            </AccessibleForm>)}
        </Container>
      </>);
};
export default CompanyCode;
